/**
 * OutlineButton — Reusable bordered secondary CTA.
 *
 * Used for secondary actions that are NOT gradient primary CTAs.
 * Example: "Download Badge" on the affiliate dashboard.
 *
 * WCAG AA: accent color defaults to #7C3AED (5.9:1 on white).
 * Touch target: minHeight 48pt meets WCAG 2.5.5.
 */

import React, { useCallback, useMemo } from 'react';
import {
  Pressable,
  Text,
  View,
  ActivityIndicator,
  StyleSheet,
  StyleProp,
  ViewStyle,
} from 'react-native';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const DEFAULT_ACCENT_COLOR = '#7C3AED';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

interface OutlineButtonProps {
  label: string;
  onPress: () => void;
  iconLeft?: React.ReactNode;
  disabled?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
  /** Border and text color. Default: '#7C3AED' (WCAG AA 5.9:1 on white) */
  accentColor?: string;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const OutlineButton: React.FC<OutlineButtonProps> = React.memo(function OutlineButton({
  label,
  onPress,
  iconLeft,
  disabled = false,
  loading = false,
  fullWidth = false,
  accentColor = DEFAULT_ACCENT_COLOR,
  style,
  accessibilityLabel,
}) {
  const handlePress = useCallback(() => {
    if (!disabled && !loading) {
      onPress();
    }
  }, [disabled, loading, onPress]);

  const containerStyle: StyleProp<ViewStyle> = useMemo(() => [
    styles.container,
    {
      borderColor: accentColor,
      alignSelf: fullWidth ? 'stretch' : 'center',
      opacity: disabled ? 0.45 : 1,
    },
    style,
  ], [accentColor, fullWidth, disabled, style]);

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled || loading}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      style={({ pressed }) => [containerStyle, pressed && styles.pressed]}
    >
      {loading ? (
        <ActivityIndicator color={accentColor} size="small" />
      ) : (
        <View style={styles.inner}>
          {iconLeft != null && (
            <View style={styles.iconLeft}>{iconLeft}</View>
          )}
          <Text style={[styles.label, { color: accentColor }]}>{label}</Text>
        </View>
      )}
    </Pressable>
  );
});

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    backgroundColor: 'transparent',
    borderWidth: 1.5,
    borderRadius: 12,
    paddingVertical: 14,
    paddingHorizontal: 20,
    minHeight: 48,
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  pressed: {
    transform: [{ scale: 0.98 }],
  } as ViewStyle,
  inner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  } as ViewStyle,
  iconLeft: {
    alignItems: 'center',
    justifyContent: 'center',
  } as ViewStyle,
  label: {
    fontSize: 15,
    fontWeight: '600',
  },
});
